import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  MenuIcon,
  ShipmentIcon,
  PickupsIcon,
  ExportDataIcon,
  TicketsIcon,
  AdvancedSearch,
  NewShipment,
  complaint,
  DataAnalysis,
  AiAssistant,
  CancelShipmentsIcon,
} from "../../assets/icons";
import { useTranslation } from "react-i18next";

export default function Sidebar() {
  const [sideOpen, setSideOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { t, i18n } = useTranslation("layout");
  const location = useLocation();

  const AuthedUser = JSON.parse(localStorage.getItem("AuthedUser")) || {};
  const { DataAnalysisLink } = AuthedUser;

  const isRTL = i18n.dir() === "rtl";

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (windowWidth < 900) {
    return null;
  }

  const menuItems = [
    { to: "/", icon: MenuIcon, label: t("Sidebar.Menu") },
    {
      to: "/reports/shipments",
      icon: ShipmentIcon,
      label: t("Sidebar.Shipments"),
    },
    { to: "/reports/pickups", icon: PickupsIcon, label: t("Sidebar.Pickups") },
    {
      to: "/reports/export",
      icon: ExportDataIcon,
      label: t("Sidebar.Reports_ExportData"),
    },
    {
      to: "/multi-search",
      icon: AdvancedSearch,
      label: t("Sidebar.Advanced_Search"),
    },
    { to: "/tickets", icon: TicketsIcon, label: t("Sidebar.Tickets") },
    {
      to: "/quick-newshipment",
      icon: NewShipment,
      label: t("Sidebar.Quick_NewShipment"),
    },
    { to: "/new-request", icon: complaint, label: t("Sidebar.New_Request") },
    {
      to: "/cancel-shipments",
      icon: CancelShipmentsIcon,
      label: t("Sidebar.Cancel_Shipments"),
    },
  ];

  if (DataAnalysisLink && DataAnalysisLink.trim() !== "") {
    menuItems.push({
      to: "/data-analysis",
      icon: DataAnalysis,
      label: t("Sidebar.data_analysis"),
    });
  }
  menuItems.push({
    to: "/ai-assistant",
    icon: AiAssistant,
    label: t("Sidebar.ai_assistant"),
  });

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        width: sideOpen ? 230 : 64,
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        height: "100vh",
        backgroundColor: "#ffffff",
        color: "#64748b",
        fontWeight: 500,
        fontSize: 13,
        position: "sticky",
        top: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: isRTL ? "none" : "1px solid #f1f5f9",
        borderLeft: isRTL ? "1px solid #f1f5f9" : "none",
        fontFamily: "'Cairo', 'Segoe UI', sans-serif",
        flexShrink: 0,
      }}
    >
      {/* Toggle Button */}
      <div
        onClick={() => setSideOpen(!sideOpen)}
        style={{
          cursor: "pointer",
          padding: "16px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            background: "#f2a900",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 14px 0 rgba(102, 126, 234, 0.4)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow =
              "0 7px 20px 0 rgba(102, 126, 234, 0.5)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow =
              "0 4px 14px 0 rgba(102, 126, 234, 0.4)";
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transition: "transform 0.3s ease",
              transform: sideOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </div>
      </div>

      {/* Menu Items */}
      <nav
        style={{
          flex: 1,
          width: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "24px 8px",
        }}
      >
        {menuItems.map(({ to, icon, label }) => {
          const active = isActive(to);
          const hovered = hoveredItem === to;

          return (
            <Link
              key={to}
              to={to}
              onMouseEnter={() => setHoveredItem(to)}
              onMouseLeave={() => setHoveredItem(null)}
              style={{
                display: "flex",
                alignItems: "center",
                padding: sideOpen ? "10px 12px" : "10px 0",
                justifyContent: sideOpen ? "flex-start" : "center",
                color: active ? "#2eafc8" : hovered ? "#334155" : "#64748b",
                textDecoration: "none",
                whiteSpace: "nowrap",
                borderRadius: 8,
                marginBottom: 2,
                backgroundColor: active
                  ? "#e6f7fa"
                  : hovered
                    ? "#f8fafc"
                    : "transparent",
                transition: "all 0.2s ease",
                position: "relative",
              }}
            >
              {active && (
                <div
                  style={{
                    position: "absolute",
                    [isRTL ? "right" : "left"]: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 3,
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: "#2eafc8",
                    transition: "all 0.2s ease",
                  }}
                />
              )}
              <img
                src={icon}
                alt={label}
                style={{
                  width: 22,
                  height: 22,
                  opacity: active ? 1 : 0.7,
                  transition: "all 0.2s ease",
                  marginRight: !isRTL && sideOpen ? 12 : 0,
                  marginLeft: isRTL && sideOpen ? 12 : 0,
                  filter: active
                    ? "brightness(0) saturate(100%) invert(66%) sepia(85%) saturate(423%) hue-rotate(145deg) brightness(89%)"
                    : "brightness(0) saturate(100%) invert(46%) sepia(12%) saturate(640%) hue-rotate(178deg) brightness(92%)",
                }}
              />
              {sideOpen && (
                <span
                  style={{
                    fontWeight: active ? 600 : 400,
                    letterSpacing: "0.01em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
